import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  headerCellStyle,
  cellStyle,
} from "../../components/tableHelpers/TableStyleUtils";
import "./TopsTables.css";

//region Helpers

/**
 * Reads UnitPrice from a quote in a safe/normalized way.
 *
 * Important:
 * - the destination top references are now UnitPrice-based;
 * - therefore this table must compare quotes using UnitPrice;
 * - BidValue is no longer the comparison source here.
 *
 * @param {object} quote - TenderQuote row.
 * @returns {number|null} Normalized UnitPrice or null.
 */
const getQuoteUnitPrice = (quote) => {
  const value = Number(quote?.UnitPrice ?? quote?.unitPrice);
  return Number.isFinite(value) ? value : null;
};

//endregion

export default function TopsByDestinationTable({ model, top }) {
  const { t } = useTranslation();

  //region Compute table data

  /**
   * Builds the destination statistics table.
   *
   * Semantics:
   * - top=1 => compare against destination top1 UnitPrice
   * - top=2 => compare against destination topN UnitPrice
   *
   * Matching rule:
   * - lower UnitPrice is better;
   * - a quote matches when quote.UnitPrice <= reference UnitPrice.
   */
  const tableData = useMemo(() => {
    const destinations = Array.from(model.quotesByDestination.keys());

    const stats = new Map();

    (model.normCarriers || []).forEach((carrier) => {
      stats.set(carrier.id, {
        name: carrier.name,
        perDestination: Object.fromEntries(destinations.map((d) => [d, "0%"])),
        total: 0,
      });
    });

    destinations.forEach((destination) => {
      const destinationQuotes = model.quotesByDestination.get(destination) || [];
      const ref = model.topByDestination.get(destination) || {
        top1: null,
        topN: null,
      };

      const refValue = top === 1 ? ref.top1 : ref.topN;

      const byCarrier = new Map();

      destinationQuotes.forEach((quote) => {
        const carrierId = quote.CarrierId ?? quote.Carrier?.Id;
        if (carrierId == null) return;

        const arr = byCarrier.get(carrierId) || [];
        arr.push(quote);
        byCarrier.set(carrierId, arr);
      });

      byCarrier.forEach((arr, carrierId) => {
        if (!stats.has(carrierId)) {
          stats.set(carrierId, {
            name: arr[0]?.CarrierName ?? String(carrierId),
            perDestination: Object.fromEntries(
              destinations.map((d) => [d, "0%"])
            ),
            total: 0,
          });
        }

        const matching =
          refValue != null
            ? arr.filter((quote) => {
              const unitPrice = getQuoteUnitPrice(quote);
              return unitPrice != null && unitPrice <= Number(refValue);
            }).length
            : 0;

        const pct = arr.length ? Math.round((matching / arr.length) * 100) : 0;

        const s = stats.get(carrierId);
        s.perDestination[destination] = `${pct}%`;
        s.total += matching;
      });
    });

    return {
      destinations,
      rows: (model.normCarriers || []).map((carrier) => {
        const s = stats.get(carrier.id) ?? {
          name: carrier.name,
          perDestination: Object.fromEntries(destinations.map((d) => [d, "0%"])),
          total: 0,
        };

        return {
          name: s.name,
          values: s.perDestination,
          total: s.total,
        };
      }),
    };
  }, [model, top]);

  //endregion

  //region Render

  return (
    <div className="tops-table-wrapper" style={{ marginTop: "40px" }}>
      <span
        style={{
          fontWeight: "bold",
          color: "var(--color-red-dark)",
          fontSize: "16px",
          display: "flex",
          marginBottom: "10px",
          borderBottom: "2px solid var(--color-red-dark)",
        }}
      >
        {t("TopDestination", { top })}
      </span>

      <div className="tops-table-container">
        <table className="tops-table tops-table-destination">
          <thead>
            <tr>
              <th style={headerCellStyle()} className="tops-col-carrier">
                {t("Carrier")}
              </th>
              {tableData.destinations.map((destination) => (
                <th
                  key={destination}
                  style={headerCellStyle()}
                  className="tops-col-main"
                >
                  {t(destination)}
                </th>
              ))}
              <th style={headerCellStyle()} className="tops-col-total">
                {t("Total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, idx) => (
              <tr key={idx}>
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-carrier tops-cell"
                >
                  {row.name}
                </td>
                {tableData.destinations.map((destination) => (
                  <td
                    key={destination}
                    style={cellStyle({ background: "var(--color-white)" })}
                    className="tops-col-main tops-cell"
                  >
                    {row.values[destination] || "0%"}
                  </td>
                ))}
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-total tops-cell"
                >
                  {row.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  //endregion
}

TopsByDestinationTable.propTypes = {
  model: PropTypes.shape({
    normCarriers: PropTypes.array.isRequired,
    quotesByDestination: PropTypes.instanceOf(Map).isRequired,
    topByDestination: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
  top: PropTypes.oneOf([1, 2]).isRequired,
};